<template>
  <span class="customer-details-trigger">
    <el-tooltip content="客户选择" placement="top">
      <i class="el-icon-zoom-in trigger-icon" @click="openDialog"></i>
    </el-tooltip>

    <el-dialog title="客户选择" :visible.sync="dialogVisible" width="980px" :close-on-click-modal="false" append-to-body @closed="handleDialogClosed">
      <el-form :model="queryParams" label-width="80px" size="small" class="query-form">
        <el-row :gutter="16">
          <el-col :span="8">
            <el-form-item label="客户名称">
              <el-input v-model="queryParams.compName" placeholder="请输入客户名称" clearable @keyup.enter.native="handleQuery" />
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="联系人">
              <el-input v-model="queryParams.contactPerson" placeholder="请输入联系人" clearable @keyup.enter.native="handleQuery" />
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="联系电话">
              <el-input v-model="queryParams.contactPhone" placeholder="请输入联系电话" clearable @keyup.enter.native="handleQuery" />
            </el-form-item>
          </el-col>
        </el-row>
        <div class="query-actions">
          <el-button type="primary" size="small" @click="handleQuery">查询</el-button>
          <el-button size="small" @click="handleReset">重置</el-button>
        </div>
      </el-form>

      <el-table ref="tableRef" v-loading="loading" :data="tableData" border row-key="customerCode" max-height="420"
                :header-cell-style="{ background: '#d8e8fb', textAlign: 'left', borderColor: '#CCC' }" @selection-change="handleSelectionChange">
        <el-table-column type="selection" width="55" reserve-selection align="center"></el-table-column>
        <el-table-column prop="customerCode" label="编号" min-width="160" show-overflow-tooltip></el-table-column>
        <el-table-column prop="compName" label="客户名称" min-width="220" show-overflow-tooltip></el-table-column>
        <el-table-column prop="contactPerson" label="联系人" min-width="120" show-overflow-tooltip></el-table-column>
        <el-table-column prop="contactPhone" label="联系电话" min-width="140" show-overflow-tooltip></el-table-column>
      </el-table>

      <el-pagination class="table-pagination" @size-change="handleSizeChange" @current-change="handleCurrentChange" :current-page="queryParams.currPage" :page-sizes="[10, 20, 50, 100]"
                     :page-size="queryParams.pageSize" layout="total, sizes, prev, pager, next, jumper" :total="total"></el-pagination>

      <div slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">取 消</el-button>
        <el-button type="primary" @click="handleConfirm">确 定</el-button>
      </div>
    </el-dialog>
  </span>
</template>

<script>
import { queryCustBaseSelectList } from '@/api/contract/contract';

export default {
  name: 'CustomerDetails',
  props: {
    value: {
      type: String,
      default: ''
    }
  },
  data () {
    return {
      dialogVisible: false,
      loading: false,
      tableData: [],
      total: 0,
      queryParams: {
        currPage: 1,
        pageSize: 10,
        compName: undefined,
        contactPerson: undefined,
        contactPhone: undefined
      },
      // 已选择的行：只用 customerCode 做 key，避免“名称/编号混用 key”导致重复和错乱
      selectedMap: {},
      // 输入框里原有的名称（当列表还没加载到这些客户时，用名称占位来做回显匹配）
      selectedNameMap: {}
    };
  },
  methods: {
    openDialog () {
      this.dialogVisible = true;
      this.queryParams.currPage = 1;
      // 每次打开都按“当前输入框值”重新初始化，避免上次勾选残留导致二次打开错乱
      this.resetSelectionStateByInput();
      this.getList();
    },
    resetSelectionStateByInput () {
      // 清空上一次的勾选缓存（弹窗只隐藏不销毁时很关键）
      this.selectedMap = {};
      this.selectedNameMap = {};

      // 清掉 table 的勾选（否则 reserve-selection 会带出上次状态）
      this.$nextTick(() => {
        if (this.$refs.tableRef && this.$refs.tableRef.clearSelection) {
          this.$refs.tableRef.clearSelection();
        }
      });

      // 再按输入框内容初始化占位名称
      this.initSelectedMapByInput();
    },
    initSelectedMapByInput () {
      const source = (this.value || '').replace(/，/g, ',');
      const names = source.split(',').map(item => item.trim()).filter(item => !!item);
      // 注意：这里无法拿到 customerCode，只能先按名称占位；
      // 等列表返回后，在 restoreSelection 里把占位转换成真正的 row（按 customerCode 存）
      const nameMap = {};
      names.forEach(name => {
        nameMap[name] = true;
      });
      this.selectedNameMap = nameMap;
    },
    getList () {
      this.loading = true;
      const payload = {
        currPage: this.queryParams.currPage,
        pageSize: this.queryParams.pageSize,
        compName: this.queryParams.compName,
        contactPerson: this.queryParams.contactPerson,
        contactTel: this.queryParams.contactPhone  // 后端字段是contactTel
      };
      queryCustBaseSelectList(payload).then(res => {
        if (res && res.retCode === 'ok' && res.additionalData) {
          const { records = [], total = 0, current = 1, size = this.queryParams.pageSize } = res.additionalData;
          this.tableData = records.map(item => ({
            customerCode: item.compCode || '',
            compName: item.compName || '',
            contactPerson: item.contactPerson || '',
            contactPhone: item.contactTel || ''
          }));
          this.total = total;
          this.queryParams.currPage = current;
          this.queryParams.pageSize = size;
          this.$nextTick(() => {
            this.restoreSelection();
          });
        } else {
          this.$message.error((res && res.retMsg) || '获取客户列表失败');
        }
        this.loading = false;
      }).catch(err => {
        console.error('获取客户列表失败:', err);
        this.loading = false;
        this.$message.error('获取客户列表失败');
      });
    },
    restoreSelection () {
      if (!this.$refs.tableRef) return;
      this.tableData.forEach(row => {
        const selected = !!this.selectedMap[row.customerCode] || !!this.selectedNameMap[row.compName];
        if (selected) {
          this.$set(this.selectedMap, row.customerCode, row);
          if (this.selectedNameMap[row.compName]) {
            this.$delete(this.selectedNameMap, row.compName);
          }
          this.$refs.tableRef.toggleRowSelection(row, true);
        }
      });
    },
    handleSelectionChange (selection) {
      this.tableData.forEach(row => {
        if (this.selectedMap[row.customerCode]) {
          this.$delete(this.selectedMap, row.customerCode);
        }
      });
      selection.forEach(row => {
        this.$set(this.selectedMap, row.customerCode, row);
        if (this.selectedNameMap[row.compName]) {
          this.$delete(this.selectedNameMap, row.compName);
        }
      });
    },
    handleQuery () {
      this.queryParams.currPage = 1;
      this.getList();
    },
    handleReset () {
      this.queryParams.compName = undefined;
      this.queryParams.contactPerson = undefined;
      this.queryParams.contactPhone = undefined;
      this.queryParams.currPage = 1;
      this.queryParams.pageSize = 100;
      this.getList();
    },
    handleSizeChange (val) {
      this.queryParams.pageSize = val;
      this.queryParams.currPage = 1;
      this.getList();
    },
    handleCurrentChange (val) {
      this.queryParams.currPage = val;
      this.getList();
    },
    handleConfirm () {
      // 先清空，再回填，避免父组件用 push/拼接导致数据累积错乱
      this.$emit('input', '');
      this.$emit('change', '');

      // 这里避免使用 Object.values / Set / Array.from，防止在某些老环境或 polyfill 配置下报错
      const names = [];
      const dedupe = {};

      // 真实选中行（按 customerCode）
      for (const code in (this.selectedMap || {})) {
        const row = this.selectedMap[code];
        const name = row && row.compName;
        if (name && !dedupe[name]) {
          dedupe[name] = true;
          names.push(name);
        }
      }

      // 占位名称（列表还没加载到）
      for (const n in (this.selectedNameMap || {})) {
        if (n && !dedupe[n]) {
          dedupe[n] = true;
          names.push(n);
        }
      }

      const result = names.join(',');
      this.$nextTick(() => {
        this.$emit('input', result);
        this.$emit('change', result);
      });
      this.dialogVisible = false;
    },
    handleDialogClosed () {
      // 关闭后也清一次，避免用户取消后再次打开仍带出旧勾选
      if (this.$refs.tableRef && this.$refs.tableRef.clearSelection) {
        this.$refs.tableRef.clearSelection();
      }
      this.selectedMap = {};
      this.selectedNameMap = {};
    }
  }
};
</script>

<style scoped>
.customer-details-trigger {
  display: inline-flex;
  align-items: center;
  /* 避免占用额外宽度导致整行被撑换行，略向左覆盖输入框右侧 */
  margin-left: -30px;
  position: relative;
  z-index: 1;
}

.trigger-icon {
  font-size: 18px;
  color: #409eff;
  cursor: pointer;
  margin-left: 8px;
}

.query-form {
  margin-bottom: 10px;
}

.query-actions {
  text-align: center;
  margin-bottom: 8px;
}

.table-pagination {
  margin-top: 16px;
  text-align: right;
}
</style>
